import { useState, useEffect, useMemo, useRef } from 'react'
import { Ticket, Plus, MessageSquare, Trash2, Send, Filter, Search, X, ChevronDown, ChevronUp, Calendar, User } from 'lucide-react'
import { adminApi } from '../services/ApiService'
import ticketService from '../services/TicketService'
import { useAuth } from '../context/AuthContext'
import './InvoicesHub.css'

const PRIORITIES = [
  { value: 'high', label: 'Alta', color: '#ef4444' },
  { value: 'medium', label: 'Media', color: '#f59e0b' },
  { value: 'low', label: 'Baja', color: '#10b981' }
]

const STATUSES = [
  { value: 'open', label: 'Abierto', color: '#3b82f6' },
  { value: 'in_progress', label: 'En Progreso', color: '#f59e0b' },
  { value: 'resolved', label: 'Resuelto', color: '#10b981' },
  { value: 'closed', label: 'Cerrado', color: '#6b7280' }
]

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'hardware', label: 'Hardware' },
  { value: 'software', label: 'Software' },
  { value: 'connectivity', label: 'Conectividad' },
  { value: 'billing', label: 'Facturación' },
  { value: 'account', label: 'Cuenta' }
]

export default function TicketsHub() {
  const { user } = useAuth()
  const [tickets, setTickets] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [filterStatus, setFilterStatus] = useState('')
  const [filterPriority, setFilterPriority] = useState('')
  const [filterMine, setFilterMine] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedTicket, setExpandedTicket] = useState(null)
  const [commentText, setCommentText] = useState('')
  const [allClients, setAllClients] = useState([])
  const [adminUsers, setAdminUsers] = useState([])
  const [clientSearch, setClientSearch] = useState('')
  const [showClientDropdown, setShowClientDropdown] = useState(false)
  const clientDropdownRef = useRef(null)
  const [form, setForm] = useState({
    subject: '', description: '', priority: 'medium', category: 'general',
    clientName: '', clientEmail: '', clientId: '', deviceMAC: '', scheduledDate: '', assignedTo: ''
  })

  const loadTickets = async () => {
    try {
      const params = {}
      if (filterStatus) params.status = filterStatus
      if (filterPriority) params.priority = filterPriority
      const data = await ticketService.listTickets(params)
      setTickets(data.tickets || [])
    } catch (e) { console.error('Error loading tickets:', e) }
  }

  const loadStats = async () => {
    try {
      const data = await ticketService.getTicketStats()
      setStats(data.stats || null)
    } catch (e) { console.error('Error loading stats:', e) }
  }

  useEffect(() => {
    Promise.all([loadTickets(), loadStats()]).then(() => setLoading(false))
    adminApi.get('/admin/clients').then(data => setAllClients(data.clients || [])).catch(() => {})
    adminApi.get('/admin/admin-users').then(data => setAdminUsers(data.users || [])).catch(() => {})
  }, [])

  useEffect(() => { loadTickets() }, [filterStatus, filterPriority])

  // Close client dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(e.target)) setShowClientDropdown(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const filteredClients = useMemo(() => {
    if (!clientSearch) return allClients.slice(0, 20)
    const s = clientSearch.toLowerCase()
    return allClients.filter(c =>
      (c.name || '').toLowerCase().includes(s) ||
      (c.email || '').toLowerCase().includes(s)
    ).slice(0, 20)
  }, [allClients, clientSearch])

  const selectClient = (client) => {
    setForm(f => ({
      ...f,
      clientName: client.name || client.email || '',
      clientEmail: client.email || '',
      clientId: client.accountId || ''
    }))
    setClientSearch(client.name || client.email || '')
    setShowClientDropdown(false)
  }

  const filteredTickets = useMemo(() => {
    let result = tickets
    if (filterMine && user?.email) {
      result = result.filter(t => t.assignedTo === user.email)
    }
    if (searchTerm) {
      const s = searchTerm.toLowerCase()
      result = result.filter(t =>
        t.ticketNumber?.toLowerCase().includes(s) ||
        t.subject?.toLowerCase().includes(s) ||
        t.clientName?.toLowerCase().includes(s) ||
        t.deviceMAC?.toLowerCase().includes(s)
      )
    }
    return result
  }, [tickets, searchTerm, filterMine, user])

  const handleCreate = async (e) => {
    e.preventDefault()
    if (!form.subject || !form.description) return
    setProcessing(true)
    try {
      await ticketService.createTicket(form)
      setForm({ subject: '', description: '', priority: 'medium', category: 'general', clientName: '', clientEmail: '', clientId: '', deviceMAC: '', scheduledDate: '', assignedTo: '' })
      setClientSearch('')
      setShowForm(false)
      await Promise.all([loadTickets(), loadStats()])
    } catch (e) { console.error('Error creating ticket:', e) }
    finally { setProcessing(false) }
  }

  const handleStatusChange = async (id, newStatus) => {
    setProcessing(true)
    try {
      await ticketService.updateTicket(id, { status: newStatus })
      await Promise.all([loadTickets(), loadStats()])
    } catch (e) { console.error('Error updating ticket:', e) }
    finally { setProcessing(false) }
  }

  const handleAddComment = async (id) => {
    if (!commentText.trim()) return
    setProcessing(true)
    try {
      await ticketService.addComment(id, { text: commentText, author: user?.name || user?.email || 'Admin' })
      setCommentText('')
      await loadTickets()
    } catch (e) { console.error('Error adding comment:', e) }
    finally { setProcessing(false) }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este ticket?')) return
    setProcessing(true)
    try {
      await ticketService.deleteTicket(id)
      if (expandedTicket === id) setExpandedTicket(null)
      await Promise.all([loadTickets(), loadStats()])
    } catch (e) { console.error('Error deleting ticket:', e) }
    finally { setProcessing(false) }
  }

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'

  if (loading) return <div className="fac-loading">Cargando tickets...</div>

  return (
    <div className="fac-container">
      <div className="fac-page">
        <div className="fac-header">
          <div>
            <h1 className="fac-title"><Ticket size={28} /> Tickets de Soporte</h1>
            <p className="fac-subtitle">Gestión de consultas y problemas de clientes</p>
          </div>
        </div>

        {/* KPIs */}
        {stats && (
          <div className="fac-kpi-row">
            <div className="fac-kpi">
              <span className="fac-kpi-value">{stats.total}</span>
              <span className="fac-kpi-label">Total</span>
            </div>
            <div className="fac-kpi">
              <span className="fac-kpi-value" style={{ color: '#3b82f6' }}>{stats.open}</span>
              <span className="fac-kpi-label">Abiertos</span>
            </div>
            <div className="fac-kpi">
              <span className="fac-kpi-value" style={{ color: '#f59e0b' }}>{stats.in_progress}</span>
              <span className="fac-kpi-label">En Progreso</span>
            </div>
            <div className="fac-kpi">
              <span className="fac-kpi-value" style={{ color: '#10b981' }}>{stats.resolved}</span>
              <span className="fac-kpi-label">Resueltos</span>
            </div>
            <div className="fac-kpi">
              <span className="fac-kpi-value" style={{ color: '#ef4444' }}>{stats.high}</span>
              <span className="fac-kpi-label">Prioridad Alta</span>
            </div>
            <div className="fac-kpi">
              <span className="fac-kpi-value">{stats.avgResolutionDays}d</span>
              <span className="fac-kpi-label">Tiempo Prom. Resolución</span>
            </div>
          </div>
        )}

        {/* Actions bar */}
        <div className="fac-section-header">
          <h3>Tickets</h3>
          <button className="fac-btn primary" onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Cancelar' : <><Plus size={16} /> Nuevo Ticket</>}
          </button>
        </div>

        {/* Filters */}
        <div className="tkt-filters">
          <div className="tkt-search-wrap">
            <Search size={16} className="tkt-search-icon" />
            <input
              placeholder="Buscar por número, asunto, cliente, MAC..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="fac-select">
            <option value="">Todos los estados</option>
            {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} className="fac-select">
            <option value="">Todas las prioridades</option>
            {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <button
            className={`fac-btn fac-btn-sm${filterMine ? ' primary' : ''}`}
            onClick={() => setFilterMine(v => !v)}
            title="Filtrar mis tickets"
          >
            <User size={14} /> Mis tickets
          </button>
          {(filterStatus || filterPriority || searchTerm || filterMine) && (
            <button className="fac-icon-btn" onClick={() => { setFilterStatus(''); setFilterPriority(''); setSearchTerm(''); setFilterMine(false) }} title="Limpiar filtros">
              <X size={16} />
            </button>
          )}
        </div>

        {/* Create Form */}
        {showForm && (
          <form className="fac-form" onSubmit={handleCreate}>
            <div className="fac-form-grid">
              <div className="fac-field fac-form-full">
                <label>Asunto *</label>
                <input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} required />
              </div>
              <div className="fac-field">
                <label>Prioridad</label>
                <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                  {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div className="fac-field">
                <label>Categoría</label>
                <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div className="fac-field" ref={clientDropdownRef} style={{ position: 'relative' }}>
                <label>Cliente</label>
                <input
                  value={clientSearch}
                  onChange={e => {
                    setClientSearch(e.target.value)
                    setForm(f => ({ ...f, clientName: e.target.value, clientId: '' }))
                    setShowClientDropdown(true)
                  }}
                  onFocus={() => setShowClientDropdown(true)}
                  placeholder="Buscar cliente..."
                  autoComplete="off"
                />
                {showClientDropdown && filteredClients.length > 0 && (
                  <div className="tkt-client-dropdown">
                    {filteredClients.map(c => (
                      <div key={c.accountId} className="tkt-client-option" onClick={() => selectClient(c)}>
                        <span className="tkt-client-option-name">{c.name || c.email || 'Sin nombre'}</span>
                        <span className="tkt-client-option-email">{c.email || ''}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="fac-field">
                <label>Email Cliente</label>
                <input type="email" value={form.clientEmail} onChange={e => setForm(f => ({ ...f, clientEmail: e.target.value }))} />
              </div>
              <div className="fac-field">
                <label>MAC Dispositivo</label>
                <select value={form.deviceMAC} onChange={e => setForm(f => ({ ...f, deviceMAC: e.target.value }))}>
                  <option value="">Sin dispositivo</option>
                  {(allClients.find(c => c.accountId === form.clientId)?.devices || []).map(d => (
                    <option key={d.mac} value={d.mac}>{d.model || 'Device'} — {d.mac}</option>
                  ))}
                </select>
              </div>
              <div className="fac-field">
                <label>Asignar a</label>
                <select value={form.assignedTo} onChange={e => setForm(f => ({ ...f, assignedTo: e.target.value }))}>
                  <option value="">Sin asignar</option>
                  {adminUsers.map(u => <option key={u.$id} value={u.email}>{u.name || u.email}</option>)}
                </select>
              </div>
              <div className="fac-field">
                <label><Calendar size={14} style={{ display: 'inline', verticalAlign: '-2px', marginRight: 4 }} />Fecha Programada</label>
                <input type="date" value={form.scheduledDate} onChange={e => setForm(f => ({ ...f, scheduledDate: e.target.value }))} />
                {form.scheduledDate && <small style={{ color: '#60a5fa', marginTop: 4 }}>📅 Se creará actividad en el calendario</small>}
              </div>
              <div className="fac-field fac-form-full">
                <label>Descripción *</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  required rows={4}
                />
              </div>
            </div>
            <div className="fac-form-actions">
              <button type="button" className="fac-btn" onClick={() => setShowForm(false)}>Cancelar</button>
              <button type="submit" className="fac-btn primary" disabled={processing}>
                {processing ? 'Creando...' : 'Crear Ticket'}
              </button>
            </div>
          </form>
        )}

        {/* Tickets List */}
        <div className="tkt-list">
          {filteredTickets.length === 0 ? (
            <div className="fac-empty">
              No hay tickets {filterStatus || filterPriority || searchTerm ? 'con los filtros aplicados' : 'registrados'}.
            </div>
          ) : (
            filteredTickets.map(ticket => {
              const priority = PRIORITIES.find(p => p.value === ticket.priority) || PRIORITIES[1]
              const status = STATUSES.find(s => s.value === ticket.status) || STATUSES[0]
              const isExpanded = expandedTicket === ticket.$id
              const comments = ticket.comments ? JSON.parse(ticket.comments) : []
              const category = CATEGORIES.find(c => c.value === ticket.category)

              return (
                <div key={ticket.$id} className={`tkt-card priority-${ticket.priority || 'medium'}${isExpanded ? ' expanded' : ''}`}>
                  {/* Ticket header row */}
                  <div className="tkt-header" onClick={() => setExpandedTicket(isExpanded ? null : ticket.$id)}>
                    <div className="tkt-header-content">
                      <div className="tkt-meta-row">
                        <span className="tkt-number">{ticket.ticketNumber}</span>
                        <span className="tkt-badge status" style={{ background: `${status.color}20`, color: status.color, borderColor: `${status.color}40` }}>
                          {status.label}
                        </span>
                        <span className="tkt-badge" style={{ background: `${priority.color}15`, color: priority.color }}>
                          {priority.label}
                        </span>
                        {category && <span className="tkt-badge category">{category.label}</span>}
                      </div>
                      <div className="tkt-subject">{ticket.subject}</div>
                      <div className="tkt-info">
                        {ticket.clientName && <span>{ticket.clientName} · </span>}
                        {formatDate(ticket.$createdAt)}
                        {ticket.scheduledDate && <span style={{ color: '#60a5fa' }}> · 📅 {new Date(ticket.scheduledDate).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' })}</span>}
                        {comments.length > 0 && <span> · <MessageSquare size={11} style={{ display: 'inline', verticalAlign: '-2px' }} /> {comments.length}</span>}
                      </div>
                    </div>
                    {isExpanded ? <ChevronUp size={18} className="tkt-chevron" /> : <ChevronDown size={18} className="tkt-chevron" />}
                  </div>

                  {/* Expanded content */}
                  {isExpanded && (
                    <div className="tkt-expanded">
                      <div className="tkt-description">{ticket.description}</div>

                      <div className="tkt-detail-meta">
                        {ticket.clientEmail && <span>Email: {ticket.clientEmail}</span>}
                        {ticket.deviceMAC && <span>MAC: <span className="mono">{ticket.deviceMAC}</span></span>}
                        <span>
                          Asignado:{' '}
                          <select
                            value={ticket.assignedTo || ''}
                            onChange={e => {
                              ticketService.updateTicket(ticket.$id, { assignedTo: e.target.value }).then(() => loadTickets())
                            }}
                            className="tkt-assign-select"
                          >
                            <option value="">Sin asignar</option>
                            {adminUsers.map(u => <option key={u.$id} value={u.email}>{u.name || u.email}</option>)}
                          </select>
                        </span>
                        {ticket.scheduledDate && <span style={{ color: '#60a5fa' }}>📅 {new Date(ticket.scheduledDate).toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' })}</span>}
                        {ticket.activityId && <span style={{ color: '#4ade80' }}>✅ Vinculado al calendario</span>}
                      </div>

                      <div className="tkt-status-actions">
                        {STATUSES.filter(s => s.value !== ticket.status).map(s => (
                          <button
                            key={s.value}
                            className="fac-btn fac-btn-sm"
                            disabled={processing}
                            onClick={() => handleStatusChange(ticket.$id, s.value)}
                            style={{ color: s.color, borderColor: `${s.color}40` }}
                          >
                            {s.label}
                          </button>
                        ))}
                        <button className="fac-icon-btn danger" disabled={processing} onClick={() => handleDelete(ticket.$id)} title="Eliminar" style={{ marginLeft: 'auto' }}>
                          <Trash2 size={14} />
                        </button>
                      </div>

                      {comments.length > 0 && (
                        <div className="tkt-comments-section">
                          <h4>Comentarios ({comments.length})</h4>
                          {comments.map(c => (
                            <div key={c.id} className="tkt-comment">
                              <div className="tkt-comment-header">
                                <span className="tkt-comment-author">{c.author}</span>
                                <span className="tkt-comment-date">{formatDate(c.createdAt)}</span>
                              </div>
                              <div className="tkt-comment-text">{c.text}</div>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="tkt-comment-input">
                        <input
                          value={commentText}
                          onChange={e => setCommentText(e.target.value)}
                          placeholder="Agregar comentario..."
                          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(ticket.$id) } }}
                        />
                        <button className="fac-btn primary" disabled={processing || !commentText.trim()} onClick={() => handleAddComment(ticket.$id)}>
                          <Send size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
